export type ToolCategory =
  | 'artisan-tools'
  | 'gaming-set'
  | 'musical-instrument'
  | 'other-tools';

export interface Tool {
  id: string;
  name: string;
  category: ToolCategory;
  cost: { amount: number; unit: 'gp' };
  weight: number;
  description?: string;
}

/**
 * SRD 5.1 Tools
 */
export const tools: Tool[] = [
  // Artisan's Tools
  {
    id: 'alchemists-supplies',
    name: "Alchemist's Supplies",
    category: 'artisan-tools',
    cost: { amount: 50, unit: 'gp' },
    weight: 8,
    description: 'Enables the creation of alchemical items like alchemist\'s fire, antitoxin, oil, perfume, and soap.',
  },
  {
    id: 'brewers-supplies',
    name: "Brewer's Supplies",
    category: 'artisan-tools',
    cost: { amount: 20, unit: 'gp' },
    weight: 9,
    description: 'Enables brewing alcoholic beverages and identifying poisoned drinks.',
  },
  {
    id: 'calligraphers-supplies',
    name: "Calligrapher's Supplies",
    category: 'artisan-tools',
    cost: { amount: 10, unit: 'gp' },
    weight: 5,
    description: 'Enables creating elegant handwriting and identifying forgeries.',
  },
  {
    id: 'carpenters-tools',
    name: "Carpenter's Tools",
    category: 'artisan-tools',
    cost: { amount: 8, unit: 'gp' },
    weight: 6,
    description: 'Enables woodworking, building structures, and identifying weak points in wooden structures.',
  },
  {
    id: 'cartographers-tools',
    name: "Cartographer's Tools",
    category: 'artisan-tools',
    cost: { amount: 15, unit: 'gp' },
    weight: 6,
    description: 'Enables creating and reading maps, measuring distances, and recalling terrain features.',
  },
  {
    id: 'cobblers-tools',
    name: "Cobbler's Tools",
    category: 'artisan-tools',
    cost: { amount: 5, unit: 'gp' },
    weight: 5,
    description: 'Enables creating and repairing shoes and leather goods.',
  },
  {
    id: 'cooks-utensils',
    name: "Cook's Utensils",
    category: 'artisan-tools',
    cost: { amount: 1, unit: 'gp' },
    weight: 8,
    description: 'Enables preparing meals that can provide comfort and potentially other benefits during rests.',
  },
  {
    id: 'glassblowers-tools',
    name: "Glassblower's Tools",
    category: 'artisan-tools',
    cost: { amount: 30, unit: 'gp' },
    weight: 5,
    description: 'Enables creating glass objects and identifying glass quality.',
  },
  {
    id: 'jewelers-tools',
    name: "Jeweler's Tools",
    category: 'artisan-tools',
    cost: { amount: 25, unit: 'gp' },
    weight: 2,
    description: 'Enables creating jewelry, appraising gems, and identifying precious metals.',
  },
  {
    id: 'leatherworkers-tools',
    name: "Leatherworker's Tools",
    category: 'artisan-tools',
    cost: { amount: 5, unit: 'gp' },
    weight: 5,
    description: 'Enables creating and repairing leather goods including armor.',
  },
  {
    id: 'masons-tools',
    name: "Mason's Tools",
    category: 'artisan-tools',
    cost: { amount: 10, unit: 'gp' },
    weight: 8,
    description: 'Enables stonework, identifying structural weaknesses, and detecting secret doors in stone.',
  },
  {
    id: 'painters-supplies',
    name: "Painter's Supplies",
    category: 'artisan-tools',
    cost: { amount: 10, unit: 'gp' },
    weight: 5,
    description: 'Enables creating paintings and reproducing images from memory.',
  },
  {
    id: 'potters-tools',
    name: "Potter's Tools",
    category: 'artisan-tools',
    cost: { amount: 10, unit: 'gp' },
    weight: 3,
    description: 'Enables creating ceramic objects and identifying pottery origins.',
  },
  {
    id: 'smiths-tools',
    name: "Smith's Tools",
    category: 'artisan-tools',
    cost: { amount: 20, unit: 'gp' },
    weight: 8,
    description: 'Enables forging metal objects, repairing metal armor and weapons.',
  },
  {
    id: 'tinkers-tools',
    name: "Tinker's Tools",
    category: 'artisan-tools',
    cost: { amount: 50, unit: 'gp' },
    weight: 10,
    description: 'Enables repairing household objects and creating small mechanical devices.',
  },
  {
    id: 'weavers-tools',
    name: "Weaver's Tools",
    category: 'artisan-tools',
    cost: { amount: 1, unit: 'gp' },
    weight: 5,
    description: 'Enables creating and repairing cloth items.',
  },
  {
    id: 'woodcarvers-tools',
    name: "Woodcarver's Tools",
    category: 'artisan-tools',
    cost: { amount: 1, unit: 'gp' },
    weight: 5,
    description: 'Enables carving wooden objects and creating arrows.',
  },

  // Gaming Sets
  {
    id: 'dice-set',
    name: 'Dice Set',
    category: 'gaming-set',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
    description: 'A set of dice used for gambling games.',
  },
  {
    id: 'dragonchess-set',
    name: 'Dragonchess Set',
    category: 'gaming-set',
    cost: { amount: 1, unit: 'gp' },
    weight: 0.5,
    description: 'A three-dimensional chess game played on three stacked boards.',
  },
  {
    id: 'playing-card-set',
    name: 'Playing Card Set',
    category: 'gaming-set',
    cost: { amount: 5, unit: 'gp' },
    weight: 0,
    description: 'A deck of cards used for various games.',
  },
  {
    id: 'three-dragon-ante-set',
    name: 'Three-Dragon Ante Set',
    category: 'gaming-set',
    cost: { amount: 1, unit: 'gp' },
    weight: 0,
    description: 'A card game popular among adventurers.',
  },

  // Musical Instruments
  {
    id: 'bagpipes',
    name: 'Bagpipes',
    category: 'musical-instrument',
    cost: { amount: 30, unit: 'gp' },
    weight: 6,
  },
  {
    id: 'drum',
    name: 'Drum',
    category: 'musical-instrument',
    cost: { amount: 6, unit: 'gp' },
    weight: 3,
  },
  {
    id: 'dulcimer',
    name: 'Dulcimer',
    category: 'musical-instrument',
    cost: { amount: 25, unit: 'gp' },
    weight: 10,
  },
  {
    id: 'flute',
    name: 'Flute',
    category: 'musical-instrument',
    cost: { amount: 2, unit: 'gp' },
    weight: 1,
  },
  {
    id: 'horn',
    name: 'Horn',
    category: 'musical-instrument',
    cost: { amount: 3, unit: 'gp' },
    weight: 2,
  },
  {
    id: 'lute',
    name: 'Lute',
    category: 'musical-instrument',
    cost: { amount: 35, unit: 'gp' },
    weight: 2,
  },
  {
    id: 'lyre',
    name: 'Lyre',
    category: 'musical-instrument',
    cost: { amount: 30, unit: 'gp' },
    weight: 2,
  },
  {
    id: 'pan-flute',
    name: 'Pan Flute',
    category: 'musical-instrument',
    cost: { amount: 12, unit: 'gp' },
    weight: 2,
  },
  {
    id: 'shawm',
    name: 'Shawm',
    category: 'musical-instrument',
    cost: { amount: 2, unit: 'gp' },
    weight: 1,
  },
  {
    id: 'viol',
    name: 'Viol',
    category: 'musical-instrument',
    cost: { amount: 30, unit: 'gp' },
    weight: 1,
  },

  // Other Tools
  {
    id: 'disguise-kit',
    name: 'Disguise Kit',
    category: 'other-tools',
    cost: { amount: 25, unit: 'gp' },
    weight: 3,
    description: 'Contains cosmetics, hair dye, small props, and clothing pieces for creating disguises.',
  },
  {
    id: 'forgery-kit',
    name: 'Forgery Kit',
    category: 'other-tools',
    cost: { amount: 15, unit: 'gp' },
    weight: 5,
    description: 'Contains papers, parchments, pens, inks, seals, and sealing wax for creating forgeries.',
  },
  {
    id: 'herbalism-kit',
    name: 'Herbalism Kit',
    category: 'other-tools',
    cost: { amount: 5, unit: 'gp' },
    weight: 3,
    description: 'Contains pouches for storing herbs, clippers, mortar and pestle, and glass jars. Required for creating antitoxin and potions of healing.',
  },
  {
    id: 'navigators-tools',
    name: "Navigator's Tools",
    category: 'other-tools',
    cost: { amount: 25, unit: 'gp' },
    weight: 2,
    description: 'Includes a sextant, compass, calipers, ruler, parchment, ink, and quill for charting courses.',
  },
  {
    id: 'poisoners-kit',
    name: "Poisoner's Kit",
    category: 'other-tools',
    cost: { amount: 50, unit: 'gp' },
    weight: 2,
    description: 'Contains vials, chemicals, and other equipment necessary for creating poisons.',
  },
  {
    id: 'thieves-tools',
    name: "Thieves' Tools",
    category: 'other-tools',
    cost: { amount: 25, unit: 'gp' },
    weight: 1,
    description: 'Contains a small file, a set of lock picks, a small mirror on a metal handle, a set of narrow-bladed scissors, and a pair of pliers.',
  },

  // Vehicles (Land & Water) - as tool proficiencies
  {
    id: 'land-vehicles',
    name: 'Vehicles (Land)',
    category: 'other-tools',
    cost: { amount: 0, unit: 'gp' },
    weight: 0,
    description: 'Proficiency with land vehicles covers driving carts, carriages, chariots, and similar vehicles.',
  },
  {
    id: 'water-vehicles',
    name: 'Vehicles (Water)',
    category: 'other-tools',
    cost: { amount: 0, unit: 'gp' },
    weight: 0,
    description: 'Proficiency with water vehicles covers sailing ships, rowboats, and similar watercraft.',
  },
];

/**
 * Get tool by ID
 */
export function getTool(id: string): Tool | undefined {
  return tools.find((t) => t.id === id);
}

/**
 * Get all tools in a category
 */
export function getToolsByCategory(category: ToolCategory): Tool[] {
  return tools.filter((t) => t.category === category);
}

/**
 * Get artisan's tools only
 */
export function getArtisanTools(): Tool[] {
  return tools.filter((t) => t.category === 'artisan-tools');
}

/**
 * Get gaming sets only
 */
export function getGamingSets(): Tool[] {
  return tools.filter((t) => t.category === 'gaming-set');
}

/**
 * Get musical instruments only
 */
export function getMusicalInstruments(): Tool[] {
  return tools.filter((t) => t.category === 'musical-instrument');
}

/**
 * Search tools by name
 */
export function searchTools(query: string): Tool[] {
  const normalized = query.toLowerCase().trim();
  return tools.filter(
    (t) =>
      t.name.toLowerCase().includes(normalized) ||
      t.id.includes(normalized)
  );
}
